import { useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import checklistIcon from '/public/tech-due-diligence/checklist.svg';
import emailIcon from '/public/tech-due-diligence/email.svg';
import breakdownIcon from '/public/tech-due-diligence/breakdown.svg';
import styles from './tech-due-diligence-quiz.module.scss';
import { LEVEL_COLORS, LEVEL_LEGEND, RED_FLAGS, SECTIONS } from './tech-due-diligence-quiz.data';
import { TOTAL_QUESTIONS } from './tech-due-diligence-quiz.step-map';
import { computeResults } from './tech-due-diligence-quiz.results';
import { Answers, Flags, Level, QuestionEntry, SendState } from './tech-due-diligence-quiz.types';
import QuestionStep from './question-step';
import RedFlagsStep, { GateCheckFlagIcon } from './red-flags-step';
import ResultsStep, { ResultFlagBanners } from './results-step';
import TechDueDiligenceForm, { TechDueDiligenceFormHandle } from '@components/shared/forms/tech-due-diligence-form';
import { CALENDLY_BOOKING_URL, FormValues } from '@components/shared/forms/tech-due-diligence-form/form.config';
import FormSubmitStatus from '@components/shared/forms/form-submit-status/form-submit-status';
import { LeadMagnetStories } from '@models/leadmagnet.types';
import { createLeadMagnet } from '@services/leadmagnet.service';
import { splitFullName } from '@helpers/split-full-name.helper';
import { openCalendlyPopup } from '@libs/integrations/calendly';
import { ButtonHandshkaeGreen } from '@components/shared/buttons/button-handshake-green';
import { Spinner } from '@components/shared/miscellaneous/spinner/spinner';

const QUIZ_META = `${TOTAL_QUESTIONS} questions · ${SECTIONS.length} sections · ~10 min`;

const QUESTIONS: QuestionEntry[] = SECTIONS.flatMap((section, sectionIndex) =>
  section.questions.map((question, qIndex) => ({ section, sectionIndex, qIndex, question })),
);

const SECTION_1_COUNT = SECTIONS[0].questions.length;
const LEAD_FORM_STEP = SECTION_1_COUNT + 1;
const TOTAL_STEPS = QUESTIONS.length + 1;
const FLAGS_START = TOTAL_STEPS + 1;
const FLAGS_END = FLAGS_START + RED_FLAGS.categories.length - 1;

const TechDueDiligenceQuiz = () => {
  const [mode, setMode] = useState<'online' | 'pdf'>('online');
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [flags, setFlags] = useState<Flags>({});
  const [leadInfo, setLeadInfo] = useState<FormValues | null>(null);
  const [editingLead, setEditingLead] = useState(false);
  const [sendState, setSendState] = useState<SendState>('idle');

  const leadFormRef = useRef<TechDueDiligenceFormHandle>(null);

  const isSubmitted = sendState === 'sent' || sendState === 'error';

  const currentEntry =
    step >= 1 && step <= SECTION_1_COUNT
      ? QUESTIONS[step - 1]
      : step > LEAD_FORM_STEP && step <= TOTAL_STEPS
        ? QUESTIONS[step - 2]
        : null;
  const questionNumber = currentEntry ? (step <= SECTION_1_COUNT ? step : step - 1) : null;
  const isLeadForm = step === LEAD_FORM_STEP;
  const showLeadSummary = isLeadForm && !!leadInfo && !editingLead;
  const currentCategory = step >= FLAGS_START && step <= FLAGS_END ? RED_FLAGS.categories[step - FLAGS_START] : null;
  const isLastCategory = step === FLAGS_END;
  const isDone = step > FLAGS_END;
  const results = useMemo(() => computeResults(answers, flags), [answers, flags]);

  function answerQuestion(entry: QuestionEntry, level: Level): void {
    setAnswers((prev) => ({ ...prev, [`${entry.section.id}:${entry.qIndex}`]: level }));
    setStep((s) => Math.min(s + 1, FLAGS_END + 1));
  }

  function toggleFlag(categoryId: string, itemIndex: number): void {
    const key = `${categoryId}:${itemIndex}`;
    setFlags((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function goBack(): void {
    setStep((s) => Math.max(s - 1, 0));
  }

  function openCalendlyIfRequested(values: FormValues): void {
    if (!values.wantsCall) return;
    const [calendlyFirstName, ...calendlyLastNameParts] = values.fullName.trim().split(/\s+/);
    openCalendlyPopup(CALENDLY_BOOKING_URL, {
      firstName: calendlyFirstName,
      lastName: calendlyLastNameParts.join(' '),
      email: values.email,
    });
  }

  async function handleSendTemplate(values: FormValues): Promise<void> {
    setSendState('working');
    try {
      const { firstName, lastName } = splitFullName(values.fullName);
      await createLeadMagnet({
        firstName,
        lastName,
        email: values.email,
        company: values.company,
        story: LeadMagnetStories.TechDueDiligence,
      });
      openCalendlyIfRequested(values);
      setSendState('sent');
    } catch (err) {
      console.error('Failed to send checklist template', err);
      setSendState('error');
    }
  }

  async function handleSendResults(): Promise<void> {
    if (!leadInfo) return;
    setSendState('working');
    try {
      const { firstName, lastName } = splitFullName(leadInfo.fullName);
      await createLeadMagnet({
        firstName,
        lastName,
        email: leadInfo.email,
        company: leadInfo.company,
        story: LeadMagnetStories.TechDueDiligence,
        answers,
        flags,
      });
      openCalendlyIfRequested(leadInfo);
      setSendState('sent');
    } catch (err) {
      console.error('Failed to send results', err);
      setSendState('error');
    }
  }

  function overallProgressFraction(): number {
    if (step === 0) return 0;
    if (step >= FLAGS_END) return 1;
    return step / FLAGS_END;
  }

  const headerProgress = (
    <div className={styles.headerProgress}>
      <div className={styles.headerProgressFill} style={{ width: `${overallProgressFraction() * 100}%` }} />
      <div className={styles.headerProgressRest} />
    </div>
  );

  return (
    <div className={styles.quiz__wrapper}>
      <div className={`${styles.quiz__wrapper__tabsBar} ${isSubmitted ? styles.hiddenKeepSpace : ''}`}>
        <div className={styles.quiz__wrapper__tabsBar__tabs}>
          <button type="button" className={mode === 'online' ? styles.active : ''} onClick={() => setMode('online')}>
            Complete Online
          </button>
          <button type="button" className={mode === 'pdf' ? styles.active : ''} onClick={() => setMode('pdf')}>
            Complete Offline
          </button>
        </div>
      </div>
      <div className={`${styles.divider} ${isSubmitted ? styles.hiddenKeepSpace : ''}`}></div>
      <div className={styles.quiz__wrapper__content}>
        {isSubmitted && (
          <div className={styles.completionOverlay}>
            <FormSubmitStatus
              status={sendState === 'sent' ? 'success' : 'failure'}
              customSuccess={{
                title: 'Done!',
                text: mode === 'online' ? 'Check your email for your results!' : 'Check your email for the guide!',
              }}
              customFailure={{
                title: 'Something went wrong',
                text: "Couldn't send the email. Please try again.",
              }}
              theme="dark"
              className={styles.completionStatus}
            />
          </div>
        )}
        <div className={isSubmitted ? styles.hiddenKeepSpace : ''}>
          {mode === 'online' && step === 0 && (
            <div className={styles.quiz__wrapper__content__online}>
              <div className={styles.quiz__wrapper__content__online__header}>
                <div className={styles.quiz__wrapper__content__online__header__title}>{QUIZ_META}</div>
                <div className={styles.quiz__wrapper__content__online__header__description}>
                  Complete and get your breakdown
                </div>
              </div>
              <div className={styles.quiz__wrapper__content__online__workflow}>
                <div className={styles.quiz__wrapper__content__online__workflow__step}>
                  <div className={styles.quiz__wrapper__content__online__workflow__step__icon}>
                    <Image src={checklistIcon} alt="" width={32} height={32} />
                  </div>
                  <div className={styles.quiz__wrapper__content__online__workflow__step__description}>
                    Take the checklist
                  </div>
                </div>
                <span className={styles.quiz__wrapper__content__online__workflow__connector} />
                <div className={styles.quiz__wrapper__content__online__workflow__step}>
                  <div className={styles.quiz__wrapper__content__online__workflow__step__icon}>
                    <Image src={emailIcon} alt="" width={32} height={32} />
                  </div>
                  <div className={styles.quiz__wrapper__content__online__workflow__step__description}>
                    Enter name
                    <br />
                    and email
                  </div>
                </div>
                <span className={styles.quiz__wrapper__content__online__workflow__connector} />
                <div className={styles.quiz__wrapper__content__online__workflow__step}>
                  <div className={styles.quiz__wrapper__content__online__workflow__step__icon}>
                    <Image src={breakdownIcon} alt="" width={32} height={32} />
                  </div>
                  <div className={styles.quiz__wrapper__content__online__workflow__step__description}>
                    Get your breakdown
                  </div>
                </div>
              </div>
              <div className={styles.quiz__wrapper__content__online__how}>
                <p className={styles.quiz__wrapper__content__online__how__text}>
                  <strong>HOW IT WORKS</strong>
                  {'   '}Answer all {TOTAL_QUESTIONS} questions by selecting a level (L4–L1). Your maturity score is
                  calculated automatically.
                </p>
                <div className={styles.quiz__wrapper__content__online__how__legend}>
                  {LEVEL_LEGEND.map(({ level, label }) => (
                    <span className={styles.quiz__wrapper__content__online__how__legend__item} key={level}>
                      <span
                        className={`${styles.dot} ${level === 'L4' ? styles.outlined : ''}`}
                        style={{ background: LEVEL_COLORS[level] }}
                      />
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
          {mode === 'online' && step >= 1 && step <= FLAGS_END && (
            <div className={`${styles.quiz__wrapper__content__online} ${currentCategory ? styles.flagsBody : ''}`}>
              <div
                className={`${styles.quiz__wrapper__content__online__header} ${
                  currentCategory ? styles.flagsHeader : ''
                }`}
                style={
                  currentCategory
                    ? { background: currentCategory.flagBgColor, color: currentCategory.flagIconColor }
                    : undefined
                }
              >
                {headerProgress}
                {currentEntry && (
                  <>
                    <div className={styles.quiz__wrapper__content__online__header__title}>
                      Section {currentEntry.sectionIndex + 1} of {SECTIONS.length} · {currentEntry.section.title}
                    </div>
                    <div className={styles.quiz__wrapper__content__online__header__description}>
                      {currentEntry.question.topic}
                    </div>
                  </>
                )}
                {isLeadForm && (
                  <>
                    <div className={styles.quiz__wrapper__content__online__header__title}>Your Details</div>
                    <div className={styles.quiz__wrapper__content__online__header__description}>
                      {showLeadSummary
                        ? 'You already filled in your details'
                        : 'Leave your contact details to continue'}
                    </div>
                  </>
                )}
                {currentCategory && (
                  <div className={styles.quiz__wrapper__content__online__header__flag}>
                    <span className={styles.flagsHeaderText}>Gate Check</span>
                    <div className={styles.quiz__wrapper__content__online__header__flag__position}>
                      {currentCategory.title}
                      <GateCheckFlagIcon fill={currentCategory.flagIconColor} />
                    </div>
                  </div>
                )}
              </div>
              {currentEntry && (
                <QuestionStep
                  key={step}
                  entry={currentEntry}
                  selected={answers[`${currentEntry.section.id}:${currentEntry.qIndex}`]}
                  onAnswer={(level) => answerQuestion(currentEntry, level)}
                />
              )}
              {isLeadForm &&
                (showLeadSummary && leadInfo ? (
                  <div className={styles.leadSummary}>
                    <div className={styles.leadSummary__row}>
                      <span className={styles.leadSummary__label}>Full name</span>
                      <span className={styles.leadSummary__value}>{leadInfo.fullName}</span>
                    </div>
                    <div className={styles.leadSummary__row}>
                      <span className={styles.leadSummary__label}>Email</span>
                      <span className={styles.leadSummary__value}>{leadInfo.email}</span>
                    </div>
                    {leadInfo.company && (
                      <div className={styles.leadSummary__row}>
                        <span className={styles.leadSummary__label}>Company</span>
                        <span className={styles.leadSummary__value}>{leadInfo.company}</span>
                      </div>
                    )}
                    <div className={styles.leadSummary__row}>
                      <span className={styles.leadSummary__label}>Book a 30 min free call with our expert</span>
                      <span className={styles.leadSummary__value}>{leadInfo.wantsCall ? 'Yes' : 'No'}</span>
                    </div>
                    <button type="button" className={styles.leadSummary__editBtn} onClick={() => setEditingLead(true)}>
                      Edit details
                    </button>
                  </div>
                ) : (
                  <TechDueDiligenceForm
                    ref={leadFormRef}
                    story={LeadMagnetStories.TechDueDiligence}
                    className={styles.pdfFormAligned}
                    disableSubmit
                    initialValues={leadInfo ?? undefined}
                    onSubmit={(values) => {
                      setLeadInfo(values);
                      setEditingLead(false);
                      setStep((s) => Math.min(s + 1, FLAGS_END + 1));
                    }}
                  />
                ))}
              {currentCategory && (
                <RedFlagsStep
                  key={step}
                  category={currentCategory}
                  flags={flags}
                  onToggle={(itemIndex) => toggleFlag(currentCategory.id, itemIndex)}
                />
              )}
            </div>
          )}
          {mode === 'online' && isDone && (
            <div className={`${styles.quiz__wrapper__content__online} ${styles.flagsBody}`}>
              <div className={`${styles.quiz__wrapper__content__online__header} ${styles.resultHeader}`}>
                <div>
                  <div className={styles.quiz__wrapper__content__online__header__title}>Result</div>
                  <div className={styles.quiz__wrapper__content__online__header__description}>
                    <span className={styles.resultScoreLabel}>Overall Maturity Score · </span>
                    {results.overall !== null ? results.overall.toFixed(2) : '—'} / 4.0
                  </div>
                </div>
                <ResultFlagBanners results={results} />
              </div>
              <ResultsStep results={results} />
            </div>
          )}
          {mode === 'pdf' && (
            <div className={styles.quiz__wrapper__content__pdf}>
              <div className={styles.quiz__wrapper__content__pdf__header}>
                <div className={styles.quiz__wrapper__content__pdf__header__title}>{QUIZ_META}</div>
                <div className={styles.quiz__wrapper__content__pdf__header__description}>
                  Get the PDF emailed to you and fill it yourself
                </div>
              </div>
              <div className={styles.quiz__wrapper__content__pdf__form}>
                <TechDueDiligenceForm
                  ref={leadFormRef}
                  story={LeadMagnetStories.TechDueDiligence}
                  className={styles.pdfFormAligned}
                  disableSubmit
                  onSubmit={handleSendTemplate}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={`${styles.footer} ${isSubmitted ? styles.hiddenKeepSpace : ''}`}>
        {mode === 'online' && isLeadForm ? (
          <>
            <button type="button" className={styles.backBtn} onClick={goBack}>
              Back
            </button>
            <ButtonHandshkaeGreen
              text="Continue"
              className={styles.continueBtn}
              action={() =>
                showLeadSummary ? setStep((s) => Math.min(s + 1, FLAGS_END + 1)) : leadFormRef.current?.submit()
              }
            />
          </>
        ) : mode === 'online' && currentCategory ? (
          <>
            <button type="button" className={styles.backBtn} onClick={goBack}>
              Back
            </button>
            <ButtonHandshkaeGreen
              text={isLastCategory ? 'See Results' : 'Next'}
              className={styles.nextBtn}
              action={() => setStep((s) => Math.min(s + 1, FLAGS_END + 1))}
            />
          </>
        ) : mode === 'online' && isDone ? (
          <>
            <button type="button" className={styles.backBtn} onClick={goBack}>
              Back
            </button>
            <button
              type="button"
              className={styles.resultsBtn}
              onClick={handleSendResults}
              disabled={sendState === 'working'}
            >
              <span style={{ visibility: sendState === 'working' ? 'hidden' : 'visible' }}>Email My Results</span>
              {sendState === 'working' && (
                <span className={styles.resultsBtnLoading}>
                  Sending
                  <Spinner className={styles.resultsBtnSpinnerWrapper} size="18px" borderWidth="2px" color="#fff" />
                </span>
              )}
            </button>
          </>
        ) : mode === 'online' && step !== 0 ? (
          <>
            <button type="button" className={styles.backBtn} onClick={goBack}>
              Back
            </button>
            <span className={styles.footerCount}>
              Question {questionNumber} of {QUESTIONS.length}
            </span>
          </>
        ) : mode === 'pdf' ? (
          <ButtonHandshkaeGreen
            text={sendState === 'working' ? 'Sending…' : 'Email Me the Checklist'}
            className={styles.startBtn}
            action={() => leadFormRef.current?.submit()}
          />
        ) : (
          <ButtonHandshkaeGreen
            text="Start Checklist"
            className={styles.startBtn}
            action={() => {
              setMode('online');
              setStep(1);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default TechDueDiligenceQuiz;
